/**
 * MLCOInboxPage.jsx — Brief 14 PR2
 *
 * MLCO Red-Flag Queue inbox UI for directors / MLCO / COLP. Mirrors the
 * list + drawer pattern from PostIntakeQueue.jsx without coupling to it.
 *
 * Role gate: in-component early-return on !(is_mlco || is_colp ||
 * is_director). Pattern matches DashboardPanels.jsx ACCOUNTS gate
 * (line ~929). No shared RoleGate component exists in this repo.
 *
 * Endpoints (PR1, already on main):
 *   GET    /api/compliance/mlco-queue?status=&severity=&q=&cursor=&limit=
 *   GET    /api/compliance/mlco-queue/{id}/risk-assessment.pdf   (BM-H631)
 *   GET    /api/compliance/mlco-queue/summary
 *   GET    /api/compliance/mlco-queue/{id}
 *   POST   /api/compliance/mlco-queue/{id}/fe-action
 *   POST   /api/compliance/mlco-queue/{id}/mlco-action
 *
 * All requests carry X-Bonsai-User-Id (set by axios interceptor in App.js).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle,
  ChevronDown,
  Loader2,
  RefreshCw,
  Search,
  ShieldAlert,
} from "lucide-react";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import { useAuth } from "../App";
// BM-H634: the item panel is shared with the Compliance Dashboard.
import DetailDrawer, {
  SEVERITY_BADGE,
  STATUS_LABEL,
  authHeaders,
  formatDate,
  itemStatusBadge,
  itemStatusLabel,
  statusLabel,
} from "../components/compliance/MLCOItemDrawer";

// Header-tile stripes (never green, amber or red: those are status colours)
// and the severity rail on each row (which is status, so it uses them).
const INBOX_HUES = [
  "linear-gradient(90deg, #6366f1, #a855f7)",
  "linear-gradient(90deg, #8b5cf6, #d946ef)",
  "linear-gradient(90deg, #3b82f6, #6366f1)",
  "linear-gradient(90deg, #06b6d4, #3b82f6)",
  "linear-gradient(90deg, #d946ef, #ec4899)",
];
const SEVERITY_RAIL = { critical: "#ef4444", high: "#f97316", medium: "#fbbf24", low: "#64748b" };

const STATUS_OPTIONS = [
  { value: "", label: "Any status" },
  { value: "pending_fe", label: STATUS_LABEL.pending_fe },
  { value: "pending_mlco", label: STATUS_LABEL.pending_mlco },
  { value: "closed", label: STATUS_LABEL.closed },
];

// Must match ALLOWED_FE_ACTIONS / ALLOWED_MLCO_OUTCOMES in compliance/mlco_queue.py.
const SEVERITY_OPTIONS = [
  { value: "", label: "Any severity" },
  { value: "critical", label: "Critical" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

// ── List page ──────────────────────────────────────────────────

export default function MLCOInboxPage() {
  const navigate = useNavigate();
  const { id: routeItemId } = useParams();
  const { userRole } = useAuth();

  const [userMeta, setUserMeta] = useState(null);
  const [metaLoading, setMetaLoading] = useState(true);

  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  const [status, setStatus] = useState("");
  const [severity, setSeverity] = useState("");
  const [q, setQ] = useState("");
  const searchTimer = useRef(null);

  const [selectedId, setSelectedId] = useState(routeItemId || null);

  // 1. Fetch role meta
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/user-role`, { headers: authHeaders() });
        if (!cancelled) setUserMeta(res.data || {});
      } catch (err) {
        if (!cancelled) setUserMeta({});
      } finally {
        if (!cancelled) setMetaLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const fetchList = useCallback(async (resetCursor = true) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (severity) params.set("severity", severity);
      if (q.trim()) params.set("q", q.trim());
      params.set("limit", "25");
      if (!resetCursor && cursor) params.set("cursor", cursor);
      const res = await axios.get(
        `${API}/compliance/mlco-queue?${params.toString()}`,
        { headers: authHeaders() }
      );
      const newItems = res.data?.items || [];
      setItems((prev) => (resetCursor ? newItems : [...prev, ...newItems]));
      setNextCursor(res.data?.next_cursor || null);
      setHasMore(!!res.data?.next_cursor);
    } catch (err) {
      toast.error(errorMessage(err, "Failed to load queue"));
    } finally {
      setLoading(false);
    }
  }, [status, severity, q, cursor]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/compliance/mlco-queue/summary`,
        { headers: authHeaders() }
      );
      setSummary(res.data || null);
    } catch {
      // soft fail — summary card just shows N/A
    }
  }, []);

  // 2. Re-fetch list whenever filters change (debounced for q)
  useEffect(() => {
    if (metaLoading) return;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setCursor(null);
      fetchList(true);
      fetchSummary();
    }, 300);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [status, severity, q, metaLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // 3. Role gate — must come after hook calls (rules of hooks).
  if (metaLoading) {
    return (
      <div className="p-6 text-slate-400 text-sm flex items-center gap-2" data-testid="mlco-inbox-loading">
        <Loader2 size={14} className="animate-spin" /> Loading…
      </div>
    );
  }
  const allowed = !!(userMeta?.is_mlco || userMeta?.is_colp || userMeta?.is_director);
  if (!allowed) {
    return (
      <div className="p-6 text-slate-300 max-w-xl" data-testid="mlco-inbox-forbidden">
        <div className="flex items-center gap-2 mb-2 text-amber-300">
          <AlertTriangle size={16} /> Not authorised
        </div>
        <p className="text-sm">
          The MLCO Inbox is only visible to directors, MLCOs and COLPs.
          You are signed in as <span className="text-slate-100">{userMeta?.email || "unknown"}</span>
          {userRole ? <> ({userRole})</> : null}.
        </p>
        <button
          type="button"
          onClick={() => navigate("/dashboard")}
          className="mt-3 px-3 py-1.5 rounded-md text-xs bg-slate-700 text-slate-200 border border-slate-600 hover:bg-slate-600"
          data-testid="mlco-inbox-back-btn"
        >
          Back to dashboard
        </button>
      </div>
    );
  }

  const onActed = () => { fetchList(true); fetchSummary(); };
  const onSelect = (item) => {
    setSelectedId(item.id);
    navigate(`/compliance/mlco-inbox/${item.id}`, { replace: false });
  };
  const onCloseDrawer = () => {
    setSelectedId(null);
    navigate(`/compliance/mlco-inbox`, { replace: false });
  };

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-screen-xl mx-auto" data-testid="mlco-inbox-page">
      <div className="cm-hero relative overflow-hidden rounded-2xl border border-white/10 px-5 py-4 flex items-center justify-between flex-wrap gap-2 shadow-xl shadow-indigo-950/40">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center ring-1 ring-white/20 shadow-lg shadow-indigo-900/50"
            style={{ background: "linear-gradient(135deg, #6366f1, #8b5cf6 55%, #3b82f6)" }}>
            <ShieldAlert size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold agents-title leading-tight">MLCO Inbox</h1>
            <p className="text-xs text-indigo-100/75">Red flags waiting for an MLCO decision, newest first</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => { fetchList(true); fetchSummary(); }}
          disabled={loading}
          className="relative px-3 py-1.5 rounded-full text-xs font-medium text-white cm-glass ring-1 ring-white/20 cm-glass-hover disabled:opacity-50"
          data-testid="mlco-inbox-refresh-btn"
        >
          <RefreshCw size={12} className={`inline-block mr-1 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="mlco-inbox-summary">
          <div className="relative overflow-hidden px-3 py-2 rounded-lg bg-navy-card border border-white/[0.07]">
            <div aria-hidden className="absolute inset-x-0 top-0 h-0.5" style={{ background: INBOX_HUES[0] }} />
            <div className="text-[10px] uppercase tracking-wider text-slate-400">Open</div>
            <div className="text-xl font-bold text-white tabular-nums" data-testid="mlco-inbox-open-count">
              {summary.pending_total ?? 0}
            </div>
          </div>
          {summary.by_status && Object.entries(summary.by_status).map(([k, v], i) => (
            <div key={k} className="relative overflow-hidden px-3 py-2 rounded-lg bg-navy-card border border-white/[0.07]">
              <div aria-hidden className="absolute inset-x-0 top-0 h-0.5" style={{ background: INBOX_HUES[(i + 1) % INBOX_HUES.length] }} />
              <div className="text-[10px] uppercase tracking-wider text-slate-400">{statusLabel(k)}</div>
              <div className="text-xl font-bold text-white tabular-nums" data-testid={`mlco-inbox-count-${k}`}>{v}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2" data-testid="mlco-inbox-filters">
        <div className="relative">
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="appearance-none pl-3 pr-8 py-1.5 rounded-md text-xs bg-slate-900/60 border border-slate-700/60 text-slate-200"
            data-testid="mlco-filter-status"
          >
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
        </div>
        <div className="relative">
          <select
            value={severity}
            onChange={(e) => setSeverity(e.target.value)}
            className="appearance-none pl-3 pr-8 py-1.5 rounded-md text-xs bg-slate-900/60 border border-slate-700/60 text-slate-200"
            data-testid="mlco-filter-severity"
          >
            {SEVERITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none" />
        </div>
        <div className="relative flex-1 min-w-[12rem]">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            placeholder="Search matter ref or name…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full pl-7 pr-3 py-1.5 rounded-md text-xs bg-slate-900/60 border border-slate-700/60 text-slate-200 placeholder-slate-500"
            data-testid="mlco-filter-search"
          />
        </div>
      </div>

      {/* List */}
      <div className="border border-white/[0.07] bg-navy-card rounded-xl overflow-hidden" data-testid="mlco-inbox-list">
        {items.length === 0 && !loading && (
          <div className="p-6 text-center text-sm text-slate-500" data-testid="mlco-inbox-empty">
            No items match the current filters.
          </div>
        )}
        {items.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => onSelect(item)}
            className={`w-full text-left flex items-start gap-3 px-3 py-2.5 border-b border-slate-700/40 last:border-b-0 border-l-[3px] hover:bg-indigo-400/10 transition-colors ${selectedId === item.id ? "bg-indigo-400/15" : ""}`}
            style={{ borderLeftColor: SEVERITY_RAIL[item.severity] || "transparent" }}
            data-testid={`mlco-inbox-row-${item.id}`}
          >
            <span className={`inline-block mt-0.5 px-1.5 py-0.5 rounded text-[10px] border ${SEVERITY_BADGE[item.severity] || ""}`}>
              {item.severity}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-slate-100 truncate">{item.matter_ref || "—"}</div>
              <div className="text-xs text-slate-400 truncate">
                {[item.matter?.client_name, item.matter?.matter_description || item.matter_name]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </div>
            <div className="text-right">
              <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] border ${itemStatusBadge(item)}`}>
                {itemStatusLabel(item)}
              </span>
              <div className="text-[10px] text-slate-500 mt-0.5">
                {formatDate(item.created_at)}
              </div>
            </div>
          </button>
        ))}
        {loading && (
          <div className="p-3 text-xs text-slate-400 flex items-center gap-2">
            <Loader2 size={12} className="animate-spin" /> Loading…
          </div>
        )}
      </div>

      {hasMore && (
        <button
          type="button"
          onClick={() => { setCursor(nextCursor); fetchList(false); }}
          className="w-full px-3 py-2 rounded-md text-xs bg-slate-800 text-slate-300 border border-slate-700/50 hover:bg-slate-700"
          data-testid="mlco-inbox-load-more-btn"
        >
          Load more
        </button>
      )}

      {selectedId && (
        <DetailDrawer
          itemId={selectedId}
          userMeta={userMeta}
          onClose={onCloseDrawer}
          onActed={onActed}
        />
      )}
    </div>
  );
}
